import { Grid, Stack, Typography } from '@mui/material';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { capitalizeFirstLetter } from '@/helpers/string';
import { useConfirm } from '@/modules/common/components/useConfirm';
import { queryItems } from '@/modules/common/helpers/search';
import { useSnackbarStore } from '@/modules/snackbar/store/useSnackbarStore';
import { SetupPropertiesSelectDialog } from '@/modules/setupV2/components/common/SetupPropertiesSelectDialog';
import { MetaBar } from '@/modules/setupV2/components/VehicleTypes/VehicleTypeSelectDialog/MetaBar';
import { VehicleCard } from '@/modules/setupV2/components/VehicleTypes/VehicleTypeSelectDialog/VehicleCard';
import {
  getNonShuttleIds,
  getShuttleSelectionChange,
  isShuttleVehicleId,
} from '@/modules/setupV2/helpers/shuttleSelectionHelpers';
import { useVehicleRemovalGuard } from '@/modules/setupV2/hooks/useVehicleRemovalGuard';
import { useVehicleStore } from '@/modules/vehicles/store/useVehicleStore';
import { UnifiedVehicle } from '@/modules/vehicles/types';
import { logger } from '@common/logging/logger';

type Props = {
  open: boolean;
  selectedVehiclesIds: string[];
  onSelect: (types: string[]) => void;
  onClose: () => void;
};

type VehicleGridProps = {
  vehicles: UnifiedVehicle[];
  selectedVehicleIds: string[];
  onClick: (id: string) => void;
};

const VehicleGridComponent = ({ vehicles, selectedVehicleIds, onClick }: VehicleGridProps) => (
  <Grid container spacing={{ xs: 2, sm: 3 }} columns={{ xs: 1, sm: 2, md: 3, lg: 4, xl: 5 }}>
    {vehicles.map((item) => (
      <Grid
        item
        xs={1}
        key={item.databaseId}
        sx={{
          '@media (min-width: 1700px)': {
            maxWidth: '16.6666%',
          },
          '@media (min-width: 2000px)': {
            maxWidth: '14.28%',
          },
          '@media (min-width: 2250px)': {
            maxWidth: '12.5%',
          },
          '@media (min-width: 2800px)': {
            maxWidth: '10%',
          },
        }}
      >
        <VehicleCard
          id={item.id}
          name={item.name}
          selected={selectedVehicleIds.includes(item.id)}
          imageUrl={item.image.url}
          onClick={onClick}
        />
      </Grid>
    ))}
  </Grid>
);

const VehicleGrid = memo(VehicleGridComponent);

const VehicleTypeSelectDialogComponent = ({
  open,
  selectedVehiclesIds,
  onClose,
  onSelect,
}: Props) => {
  const { t } = useTranslation(['interface']);
  const [innerSelectedVehiclesIds, setInnerSelectedVehiclesIds] = useState<string[]>([]);
  const innerSelectedVehiclesIdsRef = useRef<string[]>([]);
  const { allVehiclesState: allVehicles } = useVehicleStore.getState();
  const { showSnackbar } = useSnackbarStore();
  const { Dialog: ConfirmDialog, confirm } = useConfirm();
  const { canRemoveVehicle } = useVehicleRemovalGuard(confirm);

  const [searchQuery, setSearchQuery] = useState<string>('');
  const [searchHits, setSearchHits] = useState<UnifiedVehicle[]>([]);
  const [results, setResults] = useState<UnifiedVehicle[]>([]);

  const [selectedVendors, setSelectedVendors] = useState<string[]>([]);
  const [selectedApplications, setSelectedApplications] = useState<string[]>([]);
  const [selectedModels, setSelectedModels] = useState<string[]>([]);

  // get distinct filter options based on vehicles data
  const { vendors, models, applications } = useMemo(() => {
    const filterSet = allVehicles.reduce(
      (acc, nextItem) => {
        acc.vendors.add(nextItem.vendor);
        acc.models.add(nextItem.model);

        if (nextItem.tags) {
          nextItem.tags
            .filter((tag) => tag.name === 'application')
            .forEach((application) => acc.applications.add(application.value));
        }

        return acc;
      },
      {
        vendors: new Set<string>(),
        models: new Set<string>(),
        applications: new Set<string>(),
      },
    );

    return {
      vendors: Array.from(filterSet.vendors),
      models: Array.from(filterSet.models),
      applications: Array.from(filterSet.applications),
    };
  }, [allVehicles]);

  // create options to populate filters with
  const { vendorOptions, applicationOptions, modelOptions } = useMemo(() => {
    const applicationOptionLabels = t(
      'setup.floorPlan.vehicle_types_select_dialog.filters.application.options',
      {
        returnObjects: true,
      },
    );

    const getApplicationOptionLabel = (application: string): string | undefined => {
      if (typeof applicationOptionLabels !== 'object' || applicationOptionLabels === null) {
        return undefined;
      }

      const value = Reflect.get(applicationOptionLabels, application);

      return typeof value === 'string' ? value : undefined;
    };

    return {
      vendorOptions: vendors.map((vendor) => ({
        label: capitalizeFirstLetter(vendor),
        value: vendor,
      })),
      applicationOptions: applications.map((application) => {
        const labelTranslation = getApplicationOptionLabel(application);

        if (!labelTranslation) {
          // eslint-disable-next-line no-console
          logger.error(
            `Unexpected value for vehicle application. Value "${application}" has no translations yet.`,
          );
        }

        return {
          label: labelTranslation || application,
          value: application,
        };
      }),
      modelOptions: models.map((model) => ({
        label: capitalizeFirstLetter(model),
        value: model,
      })),
    };
  }, [vendors, applications, models, t]);

  // apply property filters to search hits
  useEffect(() => {
    let tempResults = searchHits;

    if (selectedVendors.length > 0) {
      tempResults = tempResults.filter((item) => selectedVendors.includes(item.vendor));
    }

    if (selectedApplications.length > 0) {
      tempResults = tempResults.filter((item) =>
        item.tags
          .filter((tag) => tag.name === 'application')
          .some((tag) => selectedApplications.includes(tag.value)),
      );
    }

    if (selectedModels.length > 0) {
      tempResults = tempResults.filter((item) => selectedModels.includes(item.model));
    }
    // PLZ FIX ME
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setResults(tempResults);
  }, [setResults, searchHits, selectedVendors, selectedApplications, selectedModels]);

  useEffect(() => {
    innerSelectedVehiclesIdsRef.current = selectedVehiclesIds;
    // PLZ FIX ME
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setInnerSelectedVehiclesIds(selectedVehiclesIds);
  }, [open, selectedVehiclesIds]);

  // apply search filter
  useEffect(() => {
    // PLZ FIX ME
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setSearchHits((currentHits) => {
      const newHits = queryItems(searchQuery, allVehicles, (item) => item.name);

      if (currentHits.length === 0 && newHits.length === 0) {
        return currentHits;
      }

      return newHits;
    });
  }, [allVehicles, searchQuery]);

  const { regularVehicleResults, shuttleVehicleResults } = useMemo(
    () => ({
      regularVehicleResults: results.filter(({ id }) => !isShuttleVehicleId(id)),
      shuttleVehicleResults: results.filter(({ id }) => isShuttleVehicleId(id)),
    }),
    [results],
  );

  const updateSelectedVehicleIds = useCallback((vehicleIds: string[]) => {
    innerSelectedVehiclesIdsRef.current = vehicleIds;
    setInnerSelectedVehiclesIds(vehicleIds);
  }, []);

  const showAtLeastOneRegularVehicleWarning = useCallback(
    () =>
      showSnackbar(
        t(
          'interface:setup.floorPlan.vehicle_types_select_dialog.warnings.at_least_one_regular_vehicle',
          'At least one regular vehicle must remain enabled.',
        ),
      ),
    [showSnackbar, t],
  );

  const onRegularVehicleClick = useCallback(
    async (id: string) => {
      const selectedVehicleIds = innerSelectedVehiclesIdsRef.current;

      if (!selectedVehicleIds.includes(id)) {
        updateSelectedVehicleIds([...selectedVehicleIds, id]);
        return;
      }

      const selectedRegularVehicleIds = getNonShuttleIds(selectedVehicleIds);

      if (selectedRegularVehicleIds.length <= 1) {
        showAtLeastOneRegularVehicleWarning();
        return;
      }

      const canDeselectSelectedVehicle = await canRemoveVehicle(id);

      if (!canDeselectSelectedVehicle) {
        return;
      }

      const currentSelectedVehicleIds = innerSelectedVehiclesIdsRef.current;

      if (!currentSelectedVehicleIds.includes(id)) {
        return;
      }

      const currentRegularVehicleIds = getNonShuttleIds(currentSelectedVehicleIds);

      if (currentRegularVehicleIds.length <= 1) {
        showAtLeastOneRegularVehicleWarning();
        return;
      }

      updateSelectedVehicleIds(currentSelectedVehicleIds.filter((selectedId) => selectedId !== id));
    },
    [canRemoveVehicle, showAtLeastOneRegularVehicleWarning, updateSelectedVehicleIds],
  );

  const onShuttleVehicleClick = useCallback(
    async (id: string) => {
      const selectedVehicleIds = innerSelectedVehiclesIdsRef.current;
      const isSelected = selectedVehicleIds.includes(id);

      if (isSelected && selectedVehicleIds.length <= 1) {
        showAtLeastOneRegularVehicleWarning();
        return;
      }

      const { nextSelectedIds, removedShuttleId } = getShuttleSelectionChange(
        selectedVehicleIds,
        id,
      );

      if (!removedShuttleId) {
        updateSelectedVehicleIds(nextSelectedIds);
        return;
      }

      const canDeselectSelectedShuttle = await canRemoveVehicle(removedShuttleId);

      if (!canDeselectSelectedShuttle) {
        return;
      }

      const currentSelectedVehicleIds = innerSelectedVehiclesIdsRef.current;

      if (!currentSelectedVehicleIds.includes(removedShuttleId)) {
        return;
      }

      const { nextSelectedIds: currentNextSelectedIds } = getShuttleSelectionChange(
        currentSelectedVehicleIds,
        id,
      );

      updateSelectedVehicleIds(currentNextSelectedIds);
    },
    [canRemoveVehicle, showAtLeastOneRegularVehicleWarning, updateSelectedVehicleIds],
  );

  return (
    <>
      <SetupPropertiesSelectDialog
        title={t('setup.floorPlan.vehicle_types_select_dialog.title')}
        innerSelectedIds={innerSelectedVehiclesIds}
        open={open}
        onSubmit={onSelect}
        onClose={onClose}
      >
        <Stack flexGrow={1} gap='10px'>
          <MetaBar
            searchQuery={searchQuery}
            setSearchQuery={setSearchQuery}
            searchHitsAmount={results.length}
            vendorOptions={vendorOptions}
            selectedVendors={selectedVendors}
            setSelectedVendors={setSelectedVendors}
            applicationOptions={applicationOptions}
            selectedApplications={selectedApplications}
            setSelectedApplications={setSelectedApplications}
            modelOptions={modelOptions}
            selectedModels={selectedModels}
            setSelectedModels={setSelectedModels}
          />

          {results.length === 0 ? (
            <Stack justifyContent='center' flexGrow={1}>
              <Typography align='center' fontSize={14}>
                {t(
                  'setup.floorPlan.vehicle_types_select_dialog.no_vehicles_found',
                  'No vehicles found',
                )}
              </Typography>
            </Stack>
          ) : (
            <Stack
              gap={5}
              padding='0px 40px 40px 40px'
              sx={{
                flexGrow: 1,
                overflowY: 'auto',
                scrollbarWidth: 'none',
                '&::-webkit-scrollbar': {
                  display: 'none',
                },
              }}
            >
              {regularVehicleResults.length > 0 && (
                <Stack gap={2}>
                  <Typography variant='h6'>
                    {t('setup.floorPlan.vehicle_types_select_dialog.sections.vehicles', 'Vehicles')}
                  </Typography>

                  <VehicleGrid
                    vehicles={regularVehicleResults}
                    selectedVehicleIds={innerSelectedVehiclesIds}
                    onClick={onRegularVehicleClick}
                  />
                </Stack>
              )}

              {shuttleVehicleResults.length > 0 && (
                <Stack gap={2}>
                  <Typography variant='h6'>
                    {t(
                      'setup.floorPlan.vehicle_types_select_dialog.sections.shuttle_vehicles',
                      'Shuttle vehicles',
                    )}
                  </Typography>

                  <VehicleGrid
                    vehicles={shuttleVehicleResults}
                    selectedVehicleIds={innerSelectedVehiclesIds}
                    onClick={onShuttleVehicleClick}
                  />
                </Stack>
              )}
            </Stack>
          )}
        </Stack>
      </SetupPropertiesSelectDialog>

      <ConfirmDialog negativeBtnTextCustom={t('common:no')} />
    </>
  );
};

export const VehicleTypeSelectDialog = memo(VehicleTypeSelectDialogComponent);
